import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { debounce } from 'lodash-es';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
  type BaseFormFieldNodeProperties,
} from '@leagueplatform/masonry-engine';
import {
  Icon,
  TextInput,
  type GDSHTMLAutocomplete,
  type GDSIconSource,
} from '@leagueplatform/genesis-core';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { formDateString } from '#utils/format-date-string.ts';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';
import { useClearValidationStatus } from '../../../hooks/use-clear-validation-status.hook';

import { MasonryFormFieldWrapper } from '../masonry-form-field-renderer/masonry-form-field-wrapper.component';

type AddonContent = {
  type: 'icon' | 'text';
  icon?: GDSIconSource;
  value: string;
};

type MasonryTextInputNodeProperties = BaseFormFieldNodeProperties & {
  placeholder?: string;
  clearable?: boolean;
  leadingContent?: AddonContent;
  trailingContent?: AddonContent;
  leadingAddon?: AddonContent;
  trailingAddon?: AddonContent;
  disabled?: boolean;
  readOnly?: boolean;
  autocomplete?: GDSHTMLAutocomplete;
  min?: string;
  max?: string;
  minLength?: number;
  maxLength?: number;
};

export type MasonryTextInputNode = MasonryEngineNode<
  'textInput',
  MasonryTextInputNodeProperties
>;

export type MasonryTextInputRendererProps =
  MasonryEngineNodeRendererProps<MasonryTextInputNode>;

export const MasonryTextInputRenderer = ({
  formFieldProperties,
  placeholder,
  value,
  clearable,
  leadingContent,
  trailingContent,
  leadingAddon,
  trailingAddon,
  disabled,
  readOnly,
  autocomplete,
  rootElementRef,
  min,
  max,
  minLength,
  maxLength,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryTextInputRendererProps) => {
  const { formFieldId, formFieldType } = formFieldProperties;
  const invokeAction = useInvokeAction();
  const clearValidationStatus = useClearValidationStatus(formFieldProperties);
  const clearInput = useRef<HTMLInputElement>(null);

  // Local state for immediate UI updates, this is used to update the UI immediately without debouncing
  const [localValue, setLocalValue] = useState(value || '');

  // Sync local state when prop value changes, this is used to update the local state when the prop value changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const setNodeState = useCallback(
    (newValue: string, event: React.SyntheticEvent) => {
      invokeAction({
        event,
        type: 'setNodeState',
        namespace: 'default',
        payload: {
          state: { value: newValue },
        },
      });
    },
    [invokeAction],
  );

  // Debounced version of setNodeState with 300ms delay, this is used to avoid excessive state updates
  const debouncedSetNodeState = useMemo(
    () => debounce(setNodeState, 300),
    [setNodeState],
  );

  // Clean up the debounced function on unmount to prevent memory leaks
  useEffect(
    () => () => {
      debouncedSetNodeState.cancel();
    },
    [debouncedSetNodeState],
  );

  const changeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value;
      setLocalValue(newValue);
      event.persist();
      debouncedSetNodeState(newValue, event);
      clearValidationStatus(event);
    },
    [clearValidationStatus, debouncedSetNodeState],
  );

  const blurHandler = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // If the user leaves the field, flush the latest visible value into
      // Masonry state immediately so follow-up actions see current input.
      debouncedSetNodeState.cancel();
      setNodeState(event.currentTarget.value, event);
    },
    [debouncedSetNodeState, setNodeState],
  );

  const onClear = useCallback(
    (event: React.SyntheticEvent) => {
      setLocalValue('');
      debouncedSetNodeState.cancel();

      const state: Record<string, unknown> = { value: '' };
      if (
        formFieldProperties.inputStatus ||
        formFieldProperties.statusMessage
      ) {
        state.formFieldProperties = {
          ...formFieldProperties,
          inputStatus: undefined,
          statusMessage: undefined,
        };
      }

      invokeAction({
        event,
        type: 'setNodeState',
        namespace: 'default',
        payload: { state },
      });
    },
    [debouncedSetNodeState, formFieldProperties, invokeAction],
  );

  const leadingAddonProps = useMemo(() => {
    if (leadingAddon?.type === 'icon') {
      return {
        addOnStartIcon: leadingAddon.icon,
        addOnStartIconLabel: leadingAddon.value,
      };
    }
    if (leadingAddon?.type === 'text') {
      return { addOnStart: leadingAddon.value };
    }
    return undefined;
  }, [leadingAddon]);

  const trailingAddonProps = useMemo(() => {
    if (trailingAddon?.type === 'icon') {
      return {
        addOnEndIcon: trailingAddon.icon,
        addOnEndIconLabel: trailingAddon.value,
      };
    }
    if (trailingAddon?.type === 'text') {
      return { addOnEnd: trailingAddon.value };
    }
    return undefined;
  }, [trailingAddon]);

  const leadingContentProps = useMemo(() => {
    if (leadingContent?.type === 'icon') {
      return <Icon icon={leadingContent.value} label={leadingContent.value} />;
    }
    if (leadingContent?.type === 'text') {
      return leadingContent.value;
    }
    return undefined;
  }, [leadingContent]);

  const trailingContentProps = useMemo(() => {
    if (trailingContent?.type === 'icon') {
      return (
        <Icon icon={trailingContent.value} label={trailingContent.value} />
      );
    }
    if (trailingContent?.type === 'text') {
      return trailingContent.value;
    }
    return undefined;
  }, [trailingContent]);

  return (
    <MasonryFormFieldWrapper
      rootElementRef={rootElementRef}
      autoComplete={autocomplete}
      formFieldProperties={formFieldProperties}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      <TextInput
        id={formFieldId}
        type={formFieldType}
        value={localValue}
        placeholder={placeholder}
        onChange={changeHandler}
        onBlur={blurHandler}
        clearable={clearable}
        onClear={onClear}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete={autocomplete}
        leadingContent={leadingContentProps}
        trailingContent={trailingContentProps}
        min={formFieldType === 'date' && min ? formDateString(min) : min}
        max={formFieldType === 'date' && max ? formDateString(max) : max}
        minLength={minLength}
        maxLength={maxLength}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...leadingAddonProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...trailingAddonProps}
        ref={clearInput}
      />
    </MasonryFormFieldWrapper>
  );
};
